import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheckCircle, FiDollarSign, FiHome, FiTool, FiZap } = FiIcons;

const BusinessOwnersPolicy = () => {
  const coverageTypes = [
    { title: 'Commercial Property Insurance', description: 'Covers property losses from common perils, office equipment, furniture, inventory, machinery, raw materials, computers and anything else vital to business operations.', icon: FiHome },
    { title: 'General Liability Insurance', description: 'Covers your company\'s legal responsibility for harm it may cause to others, up to the policy limit. Includes attorney fees and medical bills.', icon: FiShield },
    { title: 'Business Interruption Insurance', description: 'Reimburses for loss of income if a covered disaster interferes with the successful operation of your business.', icon: FiZap }
  ];

  const idealCandidates = [
    'Operate in a physical location, whether home-based or outside the home',
    'Have assets that can be stolen, including products, cash, furniture and digital property',
    'Are at a high risk for lawsuits',
    'Employ less than 100 employees and have less than $5 million in sales'
  ];

  const benefits = [
    'Less costly than buying individual policies',
    'Convenient bundled coverage',
    'Comprehensive protection for small businesses',
    'Custom-tailored to meet unique business needs',
    'Optional endorsements available'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-blue-900 bg-opacity-80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Business Owners Policy (BOP) for Small Businesses
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto"
            >
              A "package" product that combines property, business interruption, and liability insurance into one convenient, cost-effective policy for your California business.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get My Price
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is BOP Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is a Business Owners Policy?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A Business Owners Policy (BOP) is a "package" product that typically includes property, business interruption/continuation and liability insurance. Sometimes, purchasing a BOP can be a less costly option than buying individual policies.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                A home-based business or a company with only a few employees may start out with a BOP and then expand coverage as the company grows.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                <p className="text-gray-800">
                  <strong>Note:</strong> A BOP typically does not include commercial auto insurance, workers' compensation, health or disability insurance, or liability insurance for claims of wrongful professional practices.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-blue-50 p-8 rounded-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose a BOP?</h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-blue-600" />
                    <span className="text-gray-800">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Does a Business Owners Policy Cover?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A BOP generally combines three essential coverage types into one convenient bundle.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coverageTypes.map((coverage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={coverage.icon} className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{coverage.title}</h3>
                <p className="text-gray-600 text-center">{coverage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Example */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Real-World Example</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Computer Repair Shop Break-In</h4>
                <p className="text-gray-700 mb-4">
                  Assume you own a computer repair shop. In the dead of night, thieves break in and steal equipment belonging to your customers. They also steal thousands of dollars of equipment belonging to your business, such as tools, replacement components, and testing devices.
                </p>
                <p className="text-gray-700">
                  With only general liability insurance, you'd be out thousands of dollars in lost equipment. But with a Business Owners Policy, both your customers and your business would be covered, allowing you to get back to work quickly with little out-of-pocket cost.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h5 className="font-semibold text-gray-900 mb-3">BOP Would Cover:</h5>
                <ul className="space-y-2">
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Customer equipment replacement</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Your business equipment</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Property damage repairs</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" />
                    <span className="text-gray-700">Lost income during closure</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal Candidates Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Is Your Business Right for a BOP?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not every small business is a good fit for a BOP. Businesses with these characteristics are ideal candidates:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {idealCandidates.map((candidate, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4"
              >
                <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-blue-500 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{candidate}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Get Comprehensive BOP Coverage Today
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Protect your California business with a cost-effective Business Owners Policy. Coterie makes the process seamless and stress-free.
          </p>
          <div className="flex justify-center">
            <a
              href="https://coterie.com/quote"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
            >
              Get My Price
            </a>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            Monthly or annual payment options • Custom-tailored coverage • CA License #6013543
          </p>
        </div>
      </section>
    </div>
  );
};

export default BusinessOwnersPolicy;